import { responses } from './responses';
import { wasSessionExpiryHandled } from './sessionService';

/**
 * HTTP / API error shape surfaced by Axios and the project's custom
 * Kazang integration. Axios errors carry a `response` object with
 * `status` and `data`; Kazang errors use a capitalised `Error`/`Message`
 * shape on the raw error object.
 *
 * `unknown` is used at the parameter boundary so the helper functions
 * can narrow / probe for known fields without leaking an `any` contract
 * to callers.
 */
interface AxiosLikeError {
  response?: {
    status?: number;
    data?: unknown;
  };
  code?: string;
  message?: string;
  Error?: string;
  Message?: string;
}

interface ErrorMessageShape {
  error?: unknown;
  Error?: unknown;
  response_message?: unknown;
  message?: unknown;
  Print?: { toString(): string };
}

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Resolves a Tomcat/Yango API error into a user-facing message, optionally
 * shows a SweetAlert, then always throws.
 *
 * @param err - Axios-like error object.
 * @param isSweetalert - When true, suppresses the automatic `responses.throwErr` dialog.
 * @param title - Optional dialog title override.
 * @returns never — always throws `Error` with the resolved message.
 */
function throwTomcatError(
  err: AxiosLikeError,
  isSweetalert: boolean = false,
  title: string | null = null,
): never {
  let response = err.response;
  let errorMessage: unknown;
  if (response) {
    console.error(response);
    if (response.status === 404) {
      errorMessage = errorHandler.throwNotFound();
    } else {
      errorMessage = response.data;
      if (isObject(errorMessage)) {
        const shape = errorMessage as ErrorMessageShape;
        if (shape.error) {
          errorMessage = shape.error;
        } else if (shape.Error) {
          errorMessage = shape.Error;
        } else if (shape.response_message) {
          errorMessage = shape.response_message;
        } else if (shape.message) {
          errorMessage = shape.message;
        } else if (shape.Print && shape.Print.toString().includes('failed')) {
          errorMessage = shape.Print;
        }
      }
    }
  } else if (err.code === 'ECONNABORTED') {
    errorMessage = errorHandler.throwTimeOut();
  } else {
    console.error(err);
    errorMessage = err.message;
  }
  if (errorMessage === 'Network Error') {
    title = errorMessage as string;
    errorMessage = 'Please Check your Internet';
  }
  if (!isSweetalert) {
    responses.throwErr(errorMessage, title ?? undefined);
  }
  throw new Error(String(errorMessage));
}

/**
 * Normalises HTTP and integration errors into user-facing messages.
 * Exposed on `window.errorHandler` by GlobalVariables.ts.
 */
export const errorHandler = {
  /**
   * Returns the canonical message for a 404 / URL-not-found condition.
   *
   * @returns Fixed user-facing not-found string.
   */
  throwNotFound(): string {
    return 'Failed to Connect to Server! (Url not found)';
  },

  /**
   * Returns the canonical message for a request timeout condition.
   *
   * @returns Fixed user-facing timeout string.
   */
  throwTimeOut(): string {
    return 'Connection Time Out!';
  },

  /**
   * Maps franchise-login API errors to a display message.
   *
   * @param errorResponse - Axios-like error from the login endpoint.
   * @returns Server message when present, not-found text on 404, or `Unknown error`.
   */
  franchiseLoginError(errorResponse: AxiosLikeError): string {
    console.error(errorResponse);
    if (errorResponse.response?.status === 404) {
      return this.throwNotFound();
    }
    const data = errorResponse.response?.data as { message?: string } | undefined;
    return data?.message ?? 'Unknown error';
  },

  /**
   * Normalises Tomcat/Yango API errors, optionally shows a SweetAlert, then throws.
   *
   * @param err - Axios-like error object.
   * @param isSweetalert - When true, suppresses the automatic `responses.throwErr` dialog.
   * @param title - Optional dialog title override.
   * @returns void when the global session interceptor already handled expiry;
   *   otherwise delegates to {@link throwTomcatError} which always throws.
   */
  tomcatError(err: AxiosLikeError, isSweetalert: boolean = false, title: string | null = null): void {
    if (wasSessionExpiryHandled(err)) {
      return;
    }
    throwTomcatError(err, isSweetalert, title);
  },

  /**
   * Normalises Kazang customer API errors, optionally shows a SweetAlert, then throws.
   *
   * @param err - Axios-like or Kazang-shaped error object.
   * @param isSweetalert - When true, suppresses the automatic `responses.throwErr` dialog.
   * @returns never — always throws `Error` with the resolved message.
   */
  customerKazangError(err: AxiosLikeError, isSweetalert: boolean = false): never {
    console.log(err);
    let response = err.response;
    let errorMessage: unknown;
    if (response) {
      console.error(response);
      if (response.status === 404) {
        errorMessage = this.throwNotFound();
      } else {
        errorMessage = response.data;
      }
    } else if (err.Error) {
      errorMessage = err.Message;
    } else if (err.code === 'ECONNABORTED') {
      errorMessage = this.throwTimeOut();
    }
    if (!isSweetalert) {
      responses.throwErr(errorMessage);
    }
    throw new Error(String(errorMessage));
  },
};